import { createEffect, createMemo, on, untrack, type Accessor } from 'solid-js';
import { useQuery, useQueryClient } from '@tanstack/solid-query';
import { asError } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import { createFxWorkspaceReferenceStore } from './create-fx-workspace-reference-store';
import type {
  FxWorkspaceRepository,
  SyncFxWorkspaceReferencesInput,
} from './fx-workspace-repository';
import type { FxWorkspace, FxWorkspaceLoadStatus } from './fx-workspace.types';
import {
  FX_WORKSPACE_REFERENCES_QUERY_KEY,
  fxWorkspaceReferencesQueryKey,
  toQueryLoadStatus,
  type FxWorkspaceDetailsKey,
} from './fx-workspace-query';
import {
  createFxWorkspaceReferencePersistQueue,
  createFxWorkspaceReferenceToggle,
  syncFxWorkspaceReferences,
} from './fx-workspace-reference-mutations';
import {
  createRepositoryScopeGuard,
  createWorkspaceLoadTokens,
  createWorkspaceScopeGuard,
} from './fx-workspace-scope';

export interface FxWorkspaceReferencesApi {
  readonly loadStatus: Accessor<FxWorkspaceLoadStatus>;
  loadWorkspaceReferences(workspaceId: string): Promise<void>;
  isReferenceVisible(workspaceId: string, refTrn: string): boolean;
  workspaceReferenceCount(workspaceId: string): number;
  toggleReferenceVisibility(workspaceId: string, refTrn: string): void;
  addReference(workspaceId: string, refTrn: string): void;
  removeReference(workspaceId: string, refTrn: string): void;
  getWorkspaceReferences(workspaceId: string): ReadonlySet<string>;
  areWorkspaceReferencesLoaded(workspaceId: string): boolean;
  syncWorkspaceReferences(
    workspaceId: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<boolean>;
  clearWorkspace(workspaceId: string): void;
  resetRepositoryScope(): void;
}

export function useFxWorkspaceReferences(options: {
  repository: Accessor<FxWorkspaceRepository>;
  fetchKey: Accessor<string | undefined>;
  workspaces: Accessor<readonly FxWorkspace[]>;
  activeWorkspaceId: Accessor<string | undefined>;
  detailsKey: Accessor<FxWorkspaceDetailsKey | undefined>;
}): FxWorkspaceReferencesApi {
  const logger = useLogger().getLogger('FxWorkspace');
  const queryClient = useQueryClient();

  const scope = createRepositoryScopeGuard();
  const workspaceScope = createWorkspaceScopeGuard();
  const referenceLoadTokens = createWorkspaceLoadTokens();
  const persistQueue = createFxWorkspaceReferencePersistQueue();
  const store = createFxWorkspaceReferenceStore({
    queryClient,
    fetchKey: options.fetchKey,
  });

  const referencesQuery = useQuery(() => {
    const detailsKey = options.detailsKey();
    const enabled = detailsKey !== undefined;

    return {
      enabled,
      // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey + workspaceId
      queryKey:
        detailsKey === undefined
          ? ([FX_WORKSPACE_REFERENCES_QUERY_KEY, 'disabled'] as const)
          : fxWorkspaceReferencesQueryKey(
              detailsKey.key,
              detailsKey.workspaceId
            ),
      queryFn: () => {
        const workspaceId = detailsKey?.workspaceId;
        if (workspaceId === undefined) {
          return Promise.resolve([] as readonly string[]);
        }

        return untrack(options.repository).listReferences(workspaceId);
      },
      retry: false,
    };
  });

  const loadStatus = createMemo(
    (): FxWorkspaceLoadStatus => toQueryLoadStatus(referencesQuery.status)
  );

  createEffect(
    on(
      () => ({
        workspaceId: options.detailsKey()?.workspaceId,
        data: referencesQuery.data,
      }),
      (snapshot) => {
        if (snapshot.workspaceId === undefined || snapshot.data === undefined) {
          return;
        }

        if (store.areWorkspaceReferencesLoaded(snapshot.workspaceId)) {
          return;
        }

        store.applyWorkspaceReferences(snapshot.workspaceId, snapshot.data);
      }
    )
  );

  createEffect(
    on(
      () => (referencesQuery.isError ? referencesQuery.error : undefined),
      (error, previousError) => {
        if (error === undefined || error === null || error === previousError) {
          return;
        }

        logger.error(
          'Failed to load FX workspace references',
          { workspaceId: options.activeWorkspaceId() },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.references.loadFailed'),
        });
      }
    )
  );

  const loadWorkspaceReferences = async (
    workspaceId: string
  ): Promise<void> => {
    if (store.areWorkspaceReferencesLoaded(workspaceId)) {
      return;
    }

    const workspaceExists = options
      .workspaces()
      .some((workspace) => workspace.id === workspaceId);
    if (!workspaceExists) {
      return;
    }

    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    const loadToken = referenceLoadTokens.bump(workspaceId);
    const scopeSnapshot = scope.capture();

    try {
      const refTrns = await queryClient.fetchQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey + workspaceId
        queryKey: fxWorkspaceReferencesQueryKey(key, workspaceId),
        queryFn: () => untrack(options.repository).listReferences(workspaceId),
      });
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !referenceLoadTokens.isLatest(workspaceId, loadToken)
      ) {
        return;
      }

      store.applyWorkspaceReferences(workspaceId, refTrns);
    } catch (error: unknown) {
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !referenceLoadTokens.isLatest(workspaceId, loadToken)
      ) {
        return;
      }

      logger.error(
        'Failed to load FX workspace references',
        { workspaceId },
        asError(error)
      );
      if (options.activeWorkspaceId() !== workspaceId) {
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.references.loadFailed'),
        });
      }
    }
  };

  const toggleReferenceVisibility = createFxWorkspaceReferenceToggle({
    store,
    repository: options.repository,
    fetchKey: options.fetchKey,
    queryClient,
    scope,
    workspaceScope,
    persistQueue,
    logger,
  });

  const addReference = (workspaceId: string, refTrn: string): void => {
    if (store.isReferenceVisible(workspaceId, refTrn)) {
      return;
    }

    toggleReferenceVisibility(workspaceId, refTrn);
  };

  const removeReference = (workspaceId: string, refTrn: string): void => {
    if (!store.isReferenceVisible(workspaceId, refTrn)) {
      return;
    }

    toggleReferenceVisibility(workspaceId, refTrn);
  };

  const syncWorkspaceReferences = async (
    workspaceId: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<boolean> =>
    syncFxWorkspaceReferences({
      store,
      repository: options.repository,
      fetchKey: options.fetchKey,
      queryClient,
      scope,
      workspaceScope,
      logger,
      workspaceId,
      input,
    });

  const clearWorkspace = (workspaceId: string): void => {
    workspaceScope.invalidate(workspaceId);
    store.clearWorkspace(workspaceId);
    referenceLoadTokens.delete(workspaceId);
  };

  return {
    loadStatus,
    loadWorkspaceReferences,
    isReferenceVisible: store.isReferenceVisible,
    workspaceReferenceCount: store.workspaceReferenceCount,
    toggleReferenceVisibility,
    addReference,
    removeReference,
    getWorkspaceReferences: store.getWorkspaceReferences,
    areWorkspaceReferencesLoaded: store.areWorkspaceReferencesLoaded,
    syncWorkspaceReferences,
    clearWorkspace,
    resetRepositoryScope: (): void => {
      scope.invalidate();
      workspaceScope.clear();
      referenceLoadTokens.clear();
      persistQueue.clear();
      store.resetRepositoryScope();
    },
  };
}
